import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  Calendar,
  Edit,
  Trash2,
  ArrowRight,
  MessageSquare,
  Clock,
  User,
  FileText,
  Plus,
  CheckCircle,
  XCircle,
  AlertCircle,
  CreditCard,
  Users
} from 'lucide-react';
import toast from 'react-hot-toast';

const statusColors = {
  new: 'badge-info',
  contacted: 'badge-warning',
  qualified: 'badge-success',
  unqualified: 'badge-gray',
  converted: 'badge-success'
};

const abnStatusColors = {
  Active: 'bg-green-100 text-green-700',
  Cancelled: 'bg-red-100 text-red-700',
  Unverified: 'bg-yellow-100 text-yellow-700'
};

const marketingSyncStatusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  synced: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  opted_out: 'bg-gray-100 text-gray-700'
};

const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [activityData, setActivityData] = useState({
    type: 'note',
    content: ''
  });

  useEffect(() => {
    fetchLead();
    fetchActivities();
  }, [id]);

  const fetchLead = async () => {
    try {
      const response = await adminApi.getLead(id);
      setLead(response.data.data);
      setFormData(response.data.data);
    } catch (err) {
      toast.error('Failed to load lead');
      navigate('/sales/leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await adminApi.getLeadActivities(id);
      setActivities(response.data.data || []);
    } catch (err) {
      console.error('Failed to load activities:', err);
    }
  };

  const handleUpdateLead = async (e) => {
    e.preventDefault();
    try {
      await adminApi.updateLead(id, formData);
      toast.success('Lead updated successfully');
      setShowEditModal(false);
      fetchLead();
    } catch (err) {
      toast.error('Failed to update lead');
      console.error(err);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await adminApi.updateLead(id, { status: newStatus });
      toast.success('Status updated');
      fetchLead();
    } catch (err) {
      toast.error('Failed to update status');
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete "${lead.companyName}"?`)) return;

    try {
      await adminApi.deleteLead(id);
      toast.success('Lead deleted');
      navigate('/sales/leads');
    } catch (err) {
      toast.error('Failed to delete lead');
      console.error(err);
    }
  };

  const handleConvert = async () => {
    if (!confirm('Convert this lead to an opportunity?')) return;

    try {
      const response = await adminApi.convertLeadToOpportunity(id);
      toast.success('Lead converted to opportunity');
      navigate(`/sales/opportunities`);
    } catch (err) {
      toast.error('Failed to convert lead');
      console.error(err);
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    try {
      await adminApi.addLeadActivity(id, activityData);
      toast.success('Activity added');
      setShowActivityModal(false);
      setActivityData({ type: 'note', content: '' });
      fetchActivities();
    } catch (err) {
      toast.error('Failed to add activity');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Link
            to="/sales/leads"
            className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mr-4">
              <Building2 className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{lead.companyName}</h1>
              <p className="text-gray-500">{lead.contactName}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {lead.status !== 'converted' && (
            <button onClick={handleConvert} className="btn-primary">
              <ArrowRight className="w-4 h-4 mr-2" />
              Convert to Opportunity
            </button>
          )}
          <button onClick={() => setShowEditModal(true)} className="btn-secondary">
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="btn-secondary text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Card */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Lead Status</h2>
            <div className="flex items-center gap-2 mb-4">
              <span className={statusColors[lead.status] || 'badge-gray'}>
                {lead.status}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {['new', 'contacted', 'qualified', 'unqualified'].map((status) => (
                <button
                  key={status}
                  onClick={() => handleUpdateStatus(status)}
                  disabled={lead.status === status || lead.status === 'converted'}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    lead.status === status
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Activity Timeline */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Activity</h2>
              <button
                onClick={() => setShowActivityModal(true)}
                className="btn-secondary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Activity
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {activities.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  No activities recorded yet
                </div>
              ) : (
                activities.map((activity) => (
                  <div key={activity.id} className="px-6 py-4 flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      {activity.type === 'email' ? (
                        <Mail className="w-4 h-4 text-blue-500" />
                      ) : activity.type === 'call' ? (
                        <Phone className="w-4 h-4 text-green-500" />
                      ) : activity.type === 'meeting' ? (
                        <Calendar className="w-4 h-4 text-purple-500" />
                      ) : (
                        <FileText className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm text-gray-900">{activity.content}</p>
                      <div className="flex items-center mt-1 text-xs text-gray-500">
                        <User className="w-3 h-3 mr-1" />
                        {activity.createdBy || 'System'}
                        <span className="mx-2">-</span>
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(activity.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Notes */}
          {lead.notes && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
              <p className="text-gray-600 whitespace-pre-wrap">{lead.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Info */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm text-gray-500">Contact Name</dt>
                <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center">
                  <User className="w-4 h-4 mr-2 text-gray-400" />
                  {lead.firstName} {lead.lastName}
                </dd>
              </div>
              {lead.email && (
                <div>
                  <dt className="text-sm text-gray-500">Email</dt>
                  <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center">
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    <a href={`mailto:${lead.email}`} className="hover:text-primary-600">
                      {lead.email}
                    </a>
                  </dd>
                </div>
              )}
              {lead.phone && (
                <div>
                  <dt className="text-sm text-gray-500">Phone</dt>
                  <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center">
                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                    <a href={`tel:${lead.phone}`} className="hover:text-primary-600">
                      {lead.phone}
                    </a>
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-sm text-gray-500">Source</dt>
                <dd className="text-sm font-medium text-gray-900 mt-1">
                  {lead.source?.replace('_', ' ')}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Created</dt>
                <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                  {new Date(lead.createdAt).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>

          {/* ABN Status */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">ABN Status</h2>
            {lead.abn ? (
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm text-gray-500">ABN</dt>
                  <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center">
                    <CreditCard className="w-4 h-4 mr-2 text-gray-400" />
                    {lead.abn}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Status</dt>
                  <dd className="mt-1 flex items-center gap-2">
                    {lead.abnStatus === 'Active' && <CheckCircle className="w-4 h-4 text-green-600" />}
                    {lead.abnStatus === 'Cancelled' && <XCircle className="w-4 h-4 text-red-600" />}
                    {lead.abnStatus === 'Unverified' && <AlertCircle className="w-4 h-4 text-yellow-600" />}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${abnStatusColors[lead.abnStatus] || 'bg-gray-100 text-gray-700'}`}>
                      {lead.abnStatus || 'Unknown'}
                    </span>
                  </dd>
                </div>
                {lead.abnEntityName && (
                  <div>
                    <dt className="text-sm text-gray-500">Entity Name</dt>
                    <dd className="text-sm font-medium text-gray-900 mt-1">
                      {lead.abnEntityName}
                    </dd>
                  </div>
                )}
                {lead.abnEntityType && (
                  <div>
                    <dt className="text-sm text-gray-500">Entity Type</dt>
                    <dd className="text-sm font-medium text-gray-900 mt-1">
                      {lead.abnEntityType}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm text-gray-500">GST Status</dt>
                  <dd className="mt-1 flex items-center gap-2">
                    {lead.gstRegistered ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">{lead.gstStatus || 'Registered'}</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-500">{lead.gstStatus || 'Not Registered'}</span>
                      </>
                    )}
                  </dd>
                </div>
                {lead.abnVerifiedAt && (
                  <div>
                    <dt className="text-sm text-gray-500">Verified</dt>
                    <dd className="text-sm font-medium text-gray-900 mt-1">
                      {new Date(lead.abnVerifiedAt).toLocaleDateString()}
                    </dd>
                  </div>
                )}
              </dl>
            ) : (
              <p className="text-sm text-gray-500">No ABN provided</p>
            )}
          </div>

          {/* Marketing Status */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Marketing Status</h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm text-gray-500">Sync Status</dt>
                <dd className="mt-1 flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${marketingSyncStatusColors[lead.marketingSyncStatus] || 'bg-gray-100 text-gray-700'}`}>
                    {lead.marketingSyncStatus || 'pending'}
                  </span>
                </dd>
              </div>
              {lead.marketingContactId && (
                <div>
                  <dt className="text-sm text-gray-500">Marketing Contact</dt>
                  <dd className="text-sm font-medium text-green-600 mt-1 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Synced to Marketing
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Quick Actions */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-2">
              {lead.email && (
                <a
                  href={`mailto:${lead.email}`}
                  className="w-full btn-secondary justify-start"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </a>
              )}
              {lead.phone && (
                <a
                  href={`tel:${lead.phone}`}
                  className="w-full btn-secondary justify-start"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call
                </a>
              )}
              <button
                onClick={() => setShowActivityModal(true)}
                className="w-full btn-secondary justify-start"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Log Activity
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Edit Lead</h3>
            </div>

            <form onSubmit={handleUpdateLead} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  value={formData.companyName || ''}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={formData.contactName || ''}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ABN (Australian Business Number)
                </label>
                <input
                  type="text"
                  value={formData.abn || ''}
                  onChange={(e) => setFormData({ ...formData, abn: e.target.value.replace(/[^\d\s]/g, '') })}
                  className="input"
                  placeholder="00 000 000 000"
                  maxLength={14}
                />
                <p className="text-xs text-gray-500 mt-1">
                  ABN will be re-verified when updated
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Activity Modal */}
      {showActivityModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Log Activity</h3>
            </div>

            <form onSubmit={handleAddActivity} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activity Type
                </label>
                <select
                  value={activityData.type}
                  onChange={(e) => setActivityData({ ...activityData, type: e.target.value })}
                  className="input"
                >
                  <option value="note">Note</option>
                  <option value="email">Email</option>
                  <option value="call">Call</option>
                  <option value="meeting">Meeting</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Details
                </label>
                <textarea
                  value={activityData.content}
                  onChange={(e) => setActivityData({ ...activityData, content: e.target.value })}
                  className="input"
                  rows={4}
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowActivityModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Log Activity
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDetailPage;
